import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { AnchoredListbox } from './AnchoredListbox';

/**
 * The results menu of a combobox: the `<li role="option">` scaffolding, the
 * highlight, and the mouse handling that makes clicking a row work.
 *
 * Pairs with `useCombobox` — spread its `listboxProps` here. Everything about a
 * row that varies between call sites goes through `renderOption`, which returns
 * the row's *contents*; the button around it, its id, its `aria-selected` and
 * its highlight state belong to this file, because those are precisely the parts
 * the five hand-rolled copies got inconsistently right.
 *
 * The panel itself is `AnchoredListbox` rather than an absolutely-positioned
 * `<ul>`: three of the copies would be clipped by any ancestor with an overflow
 * context, which is a bug waiting for someone to put an item picker inside a
 * collapsible section. Portalling was already solved once, over there.
 */

/** What a row needs to know about itself to draw. */
export interface ComboboxOptionState {
  active: boolean;
  disabled: boolean;
  index: number;
}

interface ComboboxListboxProps<T> {
  anchorRef: React.RefObject<HTMLElement | null>;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
  open: boolean;
  id: string;
  ariaLabel: string;
  options: readonly T[];
  activeIndex: number;
  optionDomId: (index: number) => string;
  optionKey: (option: T, index: number) => string;
  isOptionDisabled: (option: T) => boolean;
  onHover: (index: number) => void;
  onPick: (option: T) => void;
  /** The row's contents. The button, its id and its aria are not yours to draw. */
  renderOption: (option: T, state: ComboboxOptionState) => ReactNode;
  /** Extra classes for the row button, for rows that are visually their own thing. */
  optionClassName?: (option: T, state: ComboboxOptionState) => string | undefined;
  /**
   * Shown in place of the rows when there are none — "searching…", "no matches".
   * A caller that passes nothing here should also leave `showsEmptyState` off, or
   * the menu opens onto an empty box.
   */
  emptyState?: ReactNode;
}

export function ComboboxListbox<T>({
  anchorRef,
  panelRef,
  open,
  id,
  ariaLabel,
  options,
  activeIndex,
  optionDomId,
  optionKey,
  isOptionDisabled,
  onHover,
  onPick,
  renderOption,
  optionClassName,
  emptyState,
}: ComboboxListboxProps<T>) {
  return (
    <AnchoredListbox anchorRef={anchorRef} panelRef={panelRef} open={open} id={id} ariaLabel={ariaLabel}>
      {options.length === 0 && emptyState !== undefined && (
        <li className="px-3 py-2.5 text-[13px] text-muted-2">{emptyState}</li>
      )}
      {options.map((option, index) => {
        const state: ComboboxOptionState = {
          active: activeIndex === index,
          disabled: isOptionDisabled(option),
          index,
        };
        return (
          <li
            key={optionKey(option, index)}
            id={optionDomId(index)}
            role="option"
            aria-selected={state.active}
            aria-disabled={state.disabled || undefined}
          >
            <button
              type="button"
              disabled={state.disabled}
              // Keeps focus in the input, so the menu does not close between
              // mousedown and click and the selection actually lands.
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => onHover(index)}
              onClick={() => onPick(option)}
              className={cn(
                'flex min-h-[44px] w-full items-center gap-2.5 px-3 text-left hover:bg-paper-2',
                state.active && 'bg-paper-2',
                state.disabled &&
                  'cursor-not-allowed opacity-50 hover:bg-transparent',
                optionClassName?.(option, state),
              )}
            >
              {renderOption(option, state)}
            </button>
          </li>
        );
      })}
    </AnchoredListbox>
  );
}
